import { type ThreeEvent } from '@react-three/fiber';
import { useCallback, useRef } from 'react';

const clickDragTollerancePixels = 1;

export const clickHasNoDrag = (
  previousX: number,
  previousY: number,
  nativeEvent: MouseEvent | PointerEvent
): boolean => {
  return (
    Math.abs(nativeEvent.clientX - previousX) <= clickDragTollerancePixels &&
    Math.abs(nativeEvent.clientY - previousY) <= clickDragTollerancePixels
  );
};

export interface UseOnClickProps {
  // user handler
  onClick: (event: ThreeEvent<PointerEvent>) => void;
  shouldHandle?: (event: ThreeEvent<PointerEvent>) => boolean;
}

export interface OnClickHandlers {
  onPointerUp: (event: ThreeEvent<PointerEvent>) => void;
  onPointerDown: (event: ThreeEvent<PointerEvent>) => void;
}

// implements onClick handler with anti-dragging logic using onPointerDown, onPointerUp
// this approach plays nicely with AbyssViewer click handlers
// calling event.stopPropagation() in onClick prevents default point picking logic under/above mesh
// can be used instead of @react/three-fiber onClick to prevent event propagation
export const useOnClick = ({ onClick, shouldHandle }: UseOnClickProps): OnClickHandlers => {
  const dragCheck = useRef({
    isStarted: false,
    previousX: 0,
    previousY: 0,
  });

  const onPointerDown = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      // console.log('onPointerDown');
      if (shouldHandle && !shouldHandle(event)) {
        return;
      }
      dragCheck.current.previousX = event.nativeEvent.clientX;
      dragCheck.current.previousY = event.nativeEvent.clientY;
      dragCheck.current.isStarted = true;
    },
    [shouldHandle]
  );

  const onPointerUp = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      // console.log('onPointerUp', event);
      const { nativeEvent } = event;
      const { isStarted, previousX, previousY } = dragCheck.current;
      if (
        isStarted &&
        clickHasNoDrag(previousX, previousY, nativeEvent) &&
        (!shouldHandle || shouldHandle(event))
      ) {
        onClick(event);
      }
      dragCheck.current.isStarted = false;
    },
    [shouldHandle, onClick]
  );

  return {
    onPointerUp,
    onPointerDown,
  };
};
